import { AudioGraph, build } from 'helicon';
import { getSeed } from '../traits';
import { repeat } from '../utils';
import {
  ADEnvelope,
  CartesianSequence,
  RandomGate,
  Reverb,
  Rhythm,
  Sequence,
  TriggeredSampler,
} from './extensions';
import { ReverbNode } from './extensions/Reverb/ReverbNode';
import { TriggeredSamplerNode } from './extensions/TriggeredSampler/TriggeredSamplerNode';

const extensions = [
  ADEnvelope,
  CartesianSequence,
  RandomGate,
  Reverb,
  Rhythm,
  Sequence,
  TriggeredSampler,
];

const graph = (includeDrums: boolean) =>
  build((node, edge) => {
    let _seed = getSeed();

    const _rand = (): number => {
      let t = (_seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
    const rand = (
      min: number = 0,
      max: number = 1,
      tilt: number = 1,
    ): number => {
      return min + Math.pow(_rand(), 1 / tilt) * (max - min);
    };
    const randInt = (min?: number, max?: number, tilt?: number) =>
      Math.floor(rand(min, max, tilt));
    const randMember = <T>(arr: Array<T>): T =>
      arr[Math.floor(_rand() * arr.length)];

    const e = (
      from: string,
      output: number,
      to: string,
      input: string | number,
      gain?: number,
    ) => {
      if (gain !== undefined) {
        const id = `${from}_${output}_${to}_${input}_AMT`;
        node(id, 'GainNode', { gain });
        edge(from, output, id, 0);
        edge(id, 0, to, input);
      } else {
        edge(from, output, to, input);
      }
    };

    const baseFreq = rand(80, 240);
    const frRand = rand();
    const oscFreq = (frRand < 0.1 ? 4 : frRand < 0.3 ? 2 : 1) * baseFreq;
    const arpFreq =
      (frRand < 0.2 ? 0 : frRand < 0.4 ? 4 : frRand < 0.5 ? 3 : 2) * baseFreq;

    const seq = repeat(5, () =>
      repeat(4, () =>
        repeat(randInt(3, rand() < 0.3 ? 6 : 5), () =>
          randMember([
            0, 200, 300, 500, 700, 1000, 1200, 1400, 1500, 1700, 1900,
          ]),
        ),
      ),
    );
    const bdRand = rand();
    const bdRtm =
      bdRand < 0.1
        ? [1, 0, 0, 0]
        : bdRand < 0.3
        ? [1, 0, 0, 0, 0]
        : bdRand < 0.4
        ? [1, 1, 0, 1, 1, 0, 1, 0]
        : [1, 0, 0];
    const hhRand = rand();
    const hhRtm =
      hhRand < 0.1
        ? [1, 0, 0]
        : hhRand < 0.2
        ? [0, 0, 1, 0]
        : hhRand < 0.4
        ? [1, 0]
        : [1];

    node('CLOCK', 'OscillatorNode', { type: 'square', frequency: rand(7, 9) });
    node('RND', 'RandomGateNode', { probability: rand(0.5, 0.8) });
    node('RND2', 'RandomGateNode', { probability: rand(0.5, 0.7) });

    node('BD_RTM', 'RhythmNode', { sequence: bdRtm });
    node('SD_RTM', 'RhythmNode', {
      sequence: [0, 0, 0, 0, 1, 0, 0, 0],
    });

    if (includeDrums) {
      node('BD', 'TriggeredSamplerNode', {
        buffer: '/bd.wav',
        detune: rand(0, 200),
      });

      node('SD', 'TriggeredSamplerNode', {
        buffer: '/sd.wav',
        detune: rand(0, 200),
      });

      node('HH', 'TriggeredSamplerNode', {
        buffer: '/hh.wav',
        detune: rand(0, 1200),
      });
      node('HH_RTM', 'RhythmNode', { sequence: hhRtm });
      node('HH_PAT', 'SequenceNode', {
        sequence: repeat(randInt(3, 8), () => rand(0.5, 1)),
      });
      node('HH_LVL', 'GainNode', { gain: 0 });
      node('HH_FLT', 'BiquadFilterNode', {
        frequency: rand(3000, 6000),
        type: 'highpass',
      });
    }

    node('OSC', 'OscillatorNode', {
      type: randMember(['sawtooth', 'square']),
      frequency: oscFreq,
    });
    node('OSC_FLT', 'BiquadFilterNode', {
      frequency: 110,
      type: 'lowpass',
      Q: 0,
    });
    node('OSC_FLT_LFO', 'OscillatorNode', {
      frequency: 1 / 16,
    });
    node('OSC_ENV', 'ADEnvelopeNode', { decay: rand(0.1, 0.4) });
    node('SEQ', 'CartesianSequenceNode', {
      sequence: seq,
    });

    node('ARP_OSC', 'OscillatorNode', {
      type: randMember(['sawtooth', 'square', 'triangle']),
      frequency: arpFreq,
    });
    node('ARP_SEQ', 'CartesianSequenceNode', {
      sequence: seq.map(square => square.map(r => r.slice(0, 3))),
    });
    node('ARP_RTM', 'RhythmNode', { sequence: rand() > 0.5 ? [1, 1, 0] : [1] });
    node('ARP_LVL', 'GainNode', { gain: 0 });
    node('ARP_FLT', 'BiquadFilterNode', { frequency: 3320 });
    node('ARP_LFO', 'OscillatorNode', { frequency: 1 / 8 });
    node('ARP_ENV', 'ADEnvelopeNode', { decay: rand(0.05, 0.15, 2) });

    node('RVB', 'ReverbNode', {
      amount: rand(0.25, 1),
      diffusion: rand(0.2, 0.7),
      lp: rand(0.7, 1),
      time: rand(0.4, 0.7),
    });
    node('RVB_IN', 'GainNode');
    node('CHM', 'ChannelMergerNode');
    node('DEST', 'AudioDestinationNode');

    const fx = (
      name: string,
      baseMult: number,
      start: number,
      end: number,
      time: number,
      level: number,
    ) => {
      node(`${name}_FX_OSC`, 'OscillatorNode', {
        frequency: baseFreq * baseMult,
        detune: end,
        type: 'triangle',
      });
      node(`${name}_FX_LVL`, 'GainNode', { gain: 0 });
      node(`${name}_FX_TRIG`, 'ConstantSourceNode', { offset: 0 });
      node(`${name}_FX_PITCH_ENV`, 'ADEnvelopeNode', {
        decay: time * rand(0.05, 0.1),
      });
      node(`${name}_FX_AMP_ENV`, 'ADEnvelopeNode', {
        decay: time * rand(0.05, 0.1),
      });

      e(`${name}_FX_OSC`, 0, `${name}_FX_LVL`, 0);
      e(`${name}_FX_TRIG`, 0, `${name}_FX_PITCH_ENV`, 'clock');
      e(`${name}_FX_TRIG`, 0, `${name}_FX_AMP_ENV`, 'clock');
      e(`${name}_FX_PITCH_ENV`, 0, `${name}_FX_OSC`, 'detune', start - end);
      e(`${name}_FX_AMP_ENV`, 0, `${name}_FX_LVL`, 'gain');
      e(`${name}_FX_LVL`, 0, 'RVB_IN', 0, level * 0.03);
      e(`${name}_FX_LVL`, 0, 'DEST', 0, level * 0.3);
    };
    fx('SHIP_SHOOT', 4, 1200, 0, 0.5, 1);
    fx('ENEMY_SHOOT', 2, 700, 1900, 0.5, 1);
    fx('SHIP_HIT', 1, 1200, 0, 5, 1.5);
    fx('ENEMY_HIT', 1, 700, 1900, 1, 1.5);
    fx('WIN', 1, -1200, 1200, 32, 2.5);
    fx('POWER_UP_APPEAR', 2, -1200, 1200, 8, 2);
    fx('POWER_UP_COLLECT', 2, 1200, -1200, 8, 2);

    e('CLOCK', 0, 'ARP_RTM', 'clock');
    e('ARP_OSC', 0, 'ARP_LVL', 0);
    e('ARP_RTM', 0, 'ARP_SEQ', 'zClock');
    e('RND2', 1, 'ARP_SEQ', 'yClock');
    e('ARP_SEQ', 0, 'ARP_OSC', 'detune');
    e('ARP_RTM', 0, 'ARP_ENV', 'clock');
    e('ARP_ENV', 0, 'ARP_LVL', 'gain');
    e('ARP_LVL', 0, 'ARP_FLT', 0);
    e('ARP_LFO', 0, 'ARP_FLT', 'detune', 600);
    e('ARP_FLT', 0, 'RVB_IN', 0, 0.03);
    e('ARP_FLT', 0, 'DEST', 0, 0.05);

    e('CLOCK', 0, 'BD_RTM', 'clock');
    e('BD_RTM', 0, 'RND', 'clock');

    e('CLOCK', 0, 'SD_RTM', 'clock');
    e('SD_RTM', 0, 'RND2', 'clock');

    if (includeDrums) {
      e('RND', 0, 'BD', 'clock');
      e('BD', 0, 'DEST', 0);

      e('RND2', 0, 'SD', 'clock');
      e('SD', 0, 'DEST', 0);
      e('SD', 0, 'RVB_IN', 0, 0.1);

      e('CLOCK', 0, 'HH_RTM', 'clock');
      e('HH_RTM', 0, 'HH', 'clock');
      e('HH_RTM', 0, 'HH_PAT', 'clock');
      e('HH_PAT', 0, 'HH_LVL', 'gain');
      e('HH', 0, 'HH_LVL', 0);
      e('HH_LVL', 0, 'HH_FLT', 0);
      e('HH_FLT', 0, 'DEST', 0, 0.15);
    }

    e('CLOCK', 0, 'SEQ', 'zClock');
    e('RND', 1, 'SEQ', 'yClock');
    e('SEQ', 0, 'OSC', 'detune');
    e('CLOCK', 0, 'OSC_ENV', 'clock');
    e('OSC_ENV', 0, 'OSC_FLT', 'detune', 4800);
    e('OSC_FLT_LFO', 0, 'OSC_FLT', 'detune', 1200);
    e('OSC', 0, 'OSC_FLT', 0);
    e('OSC_FLT', 0, 'RVB_IN', 0, 0.03);
    e('OSC_FLT', 0, 'DEST', 0, 0.2);

    e('RVB_IN', 0, 'CHM', 0);
    e('RVB_IN', 0, 'CHM', 1);
    e('CHM', 0, 'RVB', 0);
    e('RVB', 0, 'DEST', 0);
  }, extensions);

class GameMusic extends AudioGraph {
  constructor(includeDrums: boolean = true) {
    super(graph(includeDrums), extensions);
  }

  gameOver(): void {
    this.trigger('SHIP_HIT_FX_TRIG', 'offset');
    (this.audioNodes.CLOCK as OscillatorNode).frequency.setTargetAtTime(
      (this.audioNodes.CLOCK as OscillatorNode).frequency.value / 2,
      0,
      0.5,
    );
    (this.audioNodes.RVB as ReverbNode).amount.setTargetAtTime(1, 0, 2);
    (this.audioNodes.RVB as ReverbNode).time.setTargetAtTime(0.915, 0, 1.5);
    ['BD', 'HH', 'SD', 'OSC', 'ARP_OSC'].forEach(id => {
      const node = this.audioNodes[id] as TriggeredSamplerNode | OscillatorNode;
      node.detune.setTargetAtTime(node.detune.value - 1200, 0, 0.5);
    });
  }
  powerUpAppear(): void {
    this.trigger('POWER_UP_APPEAR_FX_TRIG', 'offset');
  }
  powerUpCollect(): void {
    this.trigger('POWER_UP_COLLECT_FX_TRIG', 'offset');
  }
  win(): void {
    this.trigger('WIN_FX_TRIG', 'offset');
  }
  shipShoot(): void {
    this.trigger('SHIP_SHOOT_FX_TRIG', 'offset');
  }
  enemyShoot(): void {
    this.trigger('ENEMY_SHOOT_FX_TRIG', 'offset');
  }
  shipHit(): void {
    this.trigger('SHIP_HIT_FX_TRIG', 'offset');
    ['OSC', 'ARP_OSC'].forEach(id => {
      const node = this.audioNodes[id] as OscillatorNode;
      node.detune.value = -1200;
      node.detune.setTargetAtTime(0, this.context.currentTime, 0.15);
    });
  }
  enemyHit(): void {
    this.trigger('ENEMY_HIT_FX_TRIG', 'offset');
  }
  private trigger(name: string, paramName: string): void {
    const param = (this.audioNodes[name] as any)[paramName] as AudioParam;
    param.value = 1;
    param.setValueAtTime(0, this.context.currentTime + 1 / 120);
  }
}

export const music = new GameMusic();

export const getHomePageMusic = () => new GameMusic(false);
